import {useCallback, useEffect, useRef} from 'react';

interface UseScrollFilterCollapseParams {
  isMobile: boolean;
  filtersTransitioning: boolean;
  setFiltersTransitioning: (v: boolean) => void;
  setShowFilters: (v: boolean) => void;
}

/**
 * Manages scroll-driven collapse/expand of the search filter panel on mobile.
 * Scrolling down collapses filters; scrolling up re-expands them.
 * Includes a cooldown period after animations to prevent layout-shift jitter.
 */
export function useScrollFilterCollapse({
  isMobile,
  filtersTransitioning,
  setFiltersTransitioning,
  setShowFilters,
}: UseScrollFilterCollapseParams) {
  const yPos = useRef<number | undefined>(undefined); // tracks scroll position
  const isIgnoreScroll = useRef(true); // ignore react scrolling on initial page load
  const toggleCooldownRef = useRef(false); // prevent layout-shift-induced jitter after any scroll-driven toggle
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Called when Collapse finishes entering (expand) or exiting (collapse)
  const handleCollapseTransitionEnd = useCallback(() => {
    setFiltersTransitioning(false);
    // Brief cooldown after animation to let layout fully settle before re-enabling scroll toggles
    toggleCooldownRef.current = true;
    clearTimeout(cooldownTimeoutRef.current);
    cooldownTimeoutRef.current = setTimeout(() => {
      toggleCooldownRef.current = false;
      yPos.current = window.scrollY;
    }, 200);
  }, [setFiltersTransitioning]);

  // Cleanup cooldown timeout on unmount
  useEffect(() => {
    return () => clearTimeout(cooldownTimeoutRef.current);
  }, []);

  useEffect(() => {
    const handleScrollEnd = () => {
      isIgnoreScroll.current = false;
    };

    // Use 'scroll' with a simple flag since onscrollend has limited support
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScrollEnd, 150);

      // Only toggle filters on mobile, outside of transitions, and outside post-animation cooldown
      if (!isIgnoreScroll.current && !filtersTransitioning && isMobile && !toggleCooldownRef.current) {
        const currentY = window.scrollY;
        const previousY = yPos.current ?? 0;
        const SCROLL_THRESHOLD = 15; // minimum px before toggling

        if (currentY - previousY > SCROLL_THRESHOLD) {
          // Scrolling down past threshold — collapse filters
          setShowFilters(false);
          setFiltersTransitioning(true);
          yPos.current = currentY;
        } else if (previousY - currentY > SCROLL_THRESHOLD) {
          // Scrolling up past threshold — reopen filters
          setShowFilters(true);
          setFiltersTransitioning(true);
          yPos.current = currentY;
        }
      } else {
        yPos.current = window.scrollY;
      }
    };

    document.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      clearTimeout(scrollTimeout);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [filtersTransitioning, isMobile, setFiltersTransitioning, setShowFilters]);

  return {handleCollapseTransitionEnd};
}
